/**
 * pages/bulletins.ts —— 项目快报（2026-08-25）
 * 需求唯一出处：`01_需求文档/16_项目快报方案.md`
 *
 * ## 🔴 一期那一段快报**整体作废**，不要照抄
 * | 一期 | 现在 |
 * | --- | --- |
 * | 锁定当期 | 周期由管理员**显式选择**，默认最近一个**已结束**的期 |
 * | 整体 / 单队两种范围 | **只做整体**，`scope` 那一维不存在了 |
 * | 文案只活在会话里 | **落库**，历史记录按周期分组，重新生成产生新记录 |
 * | 白底 A4 + `@media print` | 海报主视觉的深蓝纸 + **导出长图 / PDF**（打印那条路已作废） |
 * | 八个区块 | 六节，「各队进展」与「本期亮点」已合并成**各队一览** |
 *
 * ## 三条本页特有的纪律
 * 1. **纸上一个数都不在前端算**（红线 4）。纸渲染的是 `bulletin.snapshot` 里冻结的值——
 *    它是**某一时刻的事实**，跟今天的数据不一样恰恰是对的（红线 2 的例外，`16` §五）。
 * 2. **「快照过时」提示只在页面上，不进导出物**（`16` §五之四）。导出物上印的是刊头那行
 *    「数据截至」。这一条不做，就会有人把三天前的数字发出去。
 * 3. **改照片 / 改区块开关不触发 AI**（`16` §四之三）。只有「重新生成」才调模型。
 */
import type {
  Bulletin,
  BulletinPageView,
  BulletinPhoto,
  BulletinPhotoCandidate,
  BulletinText,
} from '@shared-types';
import { bulletins as api, upload as uploadApi } from '../api/index.js';
import { errText } from '../api/client.js';
import { closeModal, confirmModal, emptyHtml, openModal, toast } from '../shared/common.js';
import { $, esc, when } from '../shared/dom.js';
import { EIC, IC } from '../shared/icons.js';
import { exportPaper } from '../shared/export.js';
import * as meta from '../shared/meta.js';
import { state } from '../shared/state.js';
import { refreshCurrent } from '../router/render.js';
import { go } from '../router/index.js';
import { definePage } from './types.js';

/** 纸宽，与 `26_bulletin.css` 的 `.bp` **必须一致**——预览缩放按它算 */
const PAPER_W = 860;

/**
 * 还没生成时配置栏显示的默认值。
 * 🔴 **必须与服务端 `service.ts` 的 `DEFAULT_TOGGLES` / `defaultPhotoIds()` 一致**：
 *   配置栏上写着「默认已按每队封面各一张勾选」，界面却一个都没勾，是**自相矛盾**——
 *   用户会以为生成出来的快报里没有照片。
 * ⚠ 这不是"前端算口径"：它只是把服务端的默认值**照着显示一遍**，
 *   真正生效的那一份仍然由服务端在生成时决定。 */
const DEFAULT_TOGGLES = { stats: true, teams: true, acts: true } as const;

/* ===================== 小工具 ===================== */

/** 'YYYY-MM-DD HH:mm:ss' → 'MM-DD HH:mm'。⚠ 字符串切片，**不许 new Date()**（前端纪律 4） */
const shortAt = (s: string): string => `${s.slice(5, 10)} ${s.slice(11, 16)}`;

/**
 * 就地编辑的一段。
 * ⚠ `tabindex` 不能少：这是键盘用户进入编辑的唯一入口（一期同款）。
 * ⚠ 空文案也要渲染出这个壳子，否则那一段就再也点不开了。
 */
const ed = (key: string, t: BulletinText | undefined, tag = 'p'): string =>
  `<${tag} class="bl-ed" data-ed="${esc(key)}" tabindex="0" role="textbox">${esc(t?.text ?? '')}</${tag}>`;

/** 「AI 智能摘要」胶囊标。⚠ **只在这一段确实是模型写的时候出现**——人工改过就不该再标 */
const aiBadge = (t: BulletinText | undefined): string =>
  t?.from === 'ai'
    ? `<span class="bp-badge"><svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">` +
      `<path d="M8 0.8 9.5 5.3 14 6.8 9.5 8.3 8 12.8 6.5 8.3 2 6.8 6.5 5.3Z"></path>` +
      `<path d="M13 10.4 13.7 12.3 15.6 13 13.7 13.7 13 15.6 12.3 13.7 10.4 13 12.3 12.3Z"></path>` +
      `</svg>AI 智能摘要</span>`
    : '';

/**
 * 正在看的是不是历史版本（2026-09-28）。判据：纸上这份不是该期最新一条。
 * ⚠ 不看 hash：hash 里的 id 可能已被删、属于别的期，后端那时会回落到最新一条——以后端给的为准。
 */
const viewingOld = (v: BulletinPageView): boolean =>
  !!v.current && v.history.length > 0 && v.current.id !== v.history[0]?.id;

/** 回到该期最新一份的地址（去掉历史版本那一段） */
const latestHash = (v: BulletinPageView): string => `#/bulletins/p${v.periodNo ?? ''}`;

/* ===================== 一、纸（🔴 红线 1 的例外，只在这一段） ===================== */

/** 刊头右侧的神经网络节点图（AI 质感之一）。⚠ 纯装饰，`aria-hidden` */
const NET_SVG =
  '<svg class="bp-net" viewBox="0 0 380 220" fill="none" aria-hidden="true">' +
  '<g stroke="#4FB6F5" stroke-width="1" opacity=".42">' +
  '<path d="M26 96 L112 44 L206 76 L300 34"></path><path d="M26 96 L98 150 L206 76"></path>' +
  '<path d="M98 150 L192 186 L300 140"></path><path d="M206 76 L300 140 L360 84"></path></g>' +
  '<g fill="#8FE3FF">' +
  '<circle cx="26" cy="96" r="3.2"></circle><circle cx="112" cy="44" r="2.4"></circle>' +
  '<circle cx="206" cy="76" r="3.8"></circle><circle cx="300" cy="34" r="2.4"></circle>' +
  '<circle cx="98" cy="150" r="2.8"></circle><circle cx="192" cy="186" r="3.2"></circle>' +
  '<circle cx="300" cy="140" r="2.6"></circle><circle cx="360" cy="84" r="2.8"></circle></g></svg>';

/** 照片取不到时垫在底下的占位格（COS 未开通 / 对象被误删都会走到这里，**不是异常**） */
const PIC_PH =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<rect x="3" y="5" width="18" height="14" rx="1.5"></rect><circle cx="8.5" cy="10" r="1.8"></circle>' +
  '<path d="M3 16.5 8.5 12l4 3.5L16 12l5 4.5"></path></svg>';

/** 小节标题：斜切色块 + 标题 + 渐隐横线 + 右侧附件（AI 胶囊标或一行小字） */
const secHead = (title: string, right = ''): string =>
  `<div class="bp-st"><span class="bp-sk"></span><h2>${esc(title)}</h2>` +
  `<span class="line"></span>${right}</div>`;

/**
 * 核心指标六格。
 * ⚠ 第六格是「风险 KR」还是「需协调支持」由后端判好冻结在 `stats.focus`（2026-09-28），
 *   这里**只照着渲染，不再判一遍**。旧快照没有这两个字段，按风险 KR 显示（当时只有这一格）。
 */
function kpiHtml(b: Bulletin): string {
  const s = b.snapshot.stats;
  const sixth =
    s.focus === 'need'
      ? cellOf('需协调支持', String(s.needTeams ?? 0), '支队伍', true)
      : cellOf('风险 KR', String(s.riskKr), '条', true);
  const cell = cellOf;
  return `<div class="bp-kpi">
    ${cell('参与队伍', String(s.teams), '支')}
    ${cell('平均 OKR', `${s.avgOkr}<small>%</small>`, '等权平均')}
    ${cell('提交率', `${s.submitRate}<small>%</small>`, `${s.submitted} / ${s.expected}`)}
    ${cell('本期活动', String(s.actsInPeriod), '场')}
    ${cell('参与人次', String(s.paxInPeriod), '人次')}
    ${sixth}
  </div>`;
}

function cellOf(label: string, val: string, sub: string, risk = false): string {
  return `<div class="bp-k${risk ? ' risk' : ''}"><s>${esc(label)}</s><b class="n">${val}</b><em>${esc(sub)}</em></div>`;
}

/**
 * 各队一览：一支队一张卡，两列。
 * ⚠ 未提交的队整张卡**弱化成灰蓝**，不是标红——这是陈述事实，不是催办
 *   （同 2026-08-24 把运营总览矩阵「最新提交」列改中性灰的那条理由）。
 */
function teamsHtml(b: Bulletin): string {
  /* ⚠ 第六格显示「需协调支持」时，卡片上同步标出是哪几支队——只给一个数，读的人会问「哪几支」 */
  const needMode = b.snapshot.stats.focus === 'need';
  const cards = b.snapshot.teams
    .map((t) => {
      const okay = t.reportStatus === 'submitted';
      const risk =
        t.riskKr > 0
          ? `<span class="bp-cap risk">风险 ${t.riskKr}</span>`
          : needMode && t.hasNeed
            ? '<span class="bp-cap risk">需协调支持</span>'
            : '';
      return `<div class="bp-t${okay ? '' : ' dim'}">
        <div class="h">
          <b>${esc(t.name)}</b>
          <span class="bp-cap ${okay ? 'ok' : 'no'}">${okay ? '已提交' : '未提交'}</span>
          ${okay ? risk : ''}
          <u class="n">${t.okr}%</u>
        </div>
        <div class="topic">${esc(t.topic)}</div>
        <span class="bp-bar"><i style="width:${t.okr}%"></i>${
          okay ? `<b style="left:${t.okr}%"></b>` : ''
        }</span>
        ${ed(`hl:${t.id}`, b.sections.highlights[t.id], 'div').replace('class="bl-ed"', 'class="bl-ed hl"')}
        <div class="foot n">${t.actsInPeriod} 场活动 · ${t.paxInPeriod} 人次</div>
      </div>`;
    })
    .join('');
  /* ⚠ 2026-09-28 起按队伍编号排；此前生成的快照没有 code，是按 OKR 排的——小字跟着快照走 */
  const byCode = b.snapshot.teams.some((t) => t.code);
  return `<div class="bp-sec">
    ${secHead('各队一览', `<span class="bp-hint">${byCode ? '按队伍编号排序' : '按 OKR 进度排序'}</span>`)}
    <div class="bp-teams">${cards}</div>
  </div>`;
}

/**
 * 活动掠影。
 * ⚠ 每行张数由 `picCols` 定：行数 = ⌈n/5⌉，每行 = ⌈n/行数⌉ → 12 张 4/4/4、11 张 4/4/3、10 张 5/5，
 *   末行不满时居中。每行 4 张及以下时单张放大到 4 列宽，填满版心。
 * ⚠ 图注 = 队名 + ≤12 字短说明，**不是整句**。
 */
export function picCols(n: number): number {
  return n > 0 ? Math.ceil(n / Math.ceil(n / 5)) : 0;
}

function picsHtml(b: Bulletin, photos: BulletinPhoto[]): string {
  if (!photos.length) return '';
  const cells = photos
    .map(
      (p) => `<figure class="bp-pic">
      <div class="im">${PIC_PH}<img src="${esc(uploadApi.imgUrl(p.objectKey, 'c'))}"
        alt="${esc(p.teamName)}的活动照片" loading="lazy" decoding="async" data-imgfb></div>
      <figcaption><b>${esc(p.teamName)}</b>${ed(`cap:${p.actId}`, p.caption, 's')}</figcaption>
    </figure>`,
    )
    .join('');
  return `<div class="bp-sec">
    ${secHead('活动掠影', aiBadge(b.sections.actSummary))}
    ${ed('actSummary', b.sections.actSummary).replace('class="bl-ed"', 'class="bl-ed bp-lead"')}
    <div class="bp-pics" style="--c:${picCols(photos.length)};--w:${Math.max(picCols(photos.length), 4)}">${cells}</div>
  </div>`;
}

/**
 * 整张纸。**纯函数**：进去的是快照 + 文案，出来的是字符串。
 * ⚠ 这里渲染的全部数字都来自 `b.snapshot`，一个都不现算——快照是冻结值。
 */
export function paperHtml(b: Bulletin, photos: BulletinPhoto[]): string {
  const c = b.snapshot.cycle;
  const lock = `澎<em>π</em>计划 · 青年人才 AI 训练营`;
  return `<div class="bp" id="bp-paper">
    <div class="bp-cover">
      <span class="dots"></span><span class="g1"></span><span class="g2"></span>
      <span class="l1"></span><span class="l2"></span>
      ${NET_SVG}
      <div class="bp-top">
        <img class="bp-logo" src="/gf-logo.png" alt="广发证券" data-imgfb>
        <span class="bp-slogan">敏捷<i></i>开放<i></i>共创</span>
      </div>
      <div class="bp-head">
        <div class="bp-lock"><span class="rule"></span><span>${lock}</span></div>
        <div class="bp-title"><span class="corner"></span>
          <h1 class="bl-ed" data-ed="title" tabindex="0" role="textbox">${esc(b.sections.title)}</h1>
        </div>
        <p class="bp-when n">${esc(c.start)} — ${esc(c.end)}
          <i>|</i> 数据截至 ${esc(c.asOf.slice(0, 16))}</p>
      </div>
    </div>

    <div class="bp-body">
      ${when(b.toggles.stats, kpiHtml(b))}

      <div class="bp-sec">
        ${secHead('本期综述', aiBadge(b.sections.intro))}
        <div class="bp-card">
          ${ed('intro', b.sections.intro)}
          ${ed('dataNote', b.sections.dataNote, 'p').replace('class="bl-ed"', 'class="bl-ed bp-hint"')}
        </div>
      </div>

      ${when(b.toggles.teams, teamsHtml(b))}
      ${when(b.toggles.acts, picsHtml(b, photos))}

      <div class="bp-end">${ed('closing', b.sections.closing)}</div>
    </div>

    <div class="bp-foot">
      <span class="bp-grid"></span><span class="bp-veil"></span>
      <div class="bp-row">
        <span class="bp-name">澎<em>π</em>计划 · 青年人才 AI 训练营</span>
        <span class="bp-by n">AI 训练营项目组 · ${esc(c.asOf.slice(0, 10))} 生成</span>
      </div>
    </div>
  </div>`;
}

/* ===================== 二、配置栏 ===================== */

const CHECK = `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4"
  stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m3.4 8.4 3 3 6.2-6.6"/></svg>`;

/** ① 周期。⚠ 进行中的期**仍可选**，只是标注一句（`16` §九） */
function periodGroup(v: BulletinPageView): string {
  const opts = v.periods
    .map(
      (p) =>
        `<option value="${p.no}"${p.no === v.periodNo ? ' selected' : ''}>` +
        `${esc(p.name)}（${esc(p.start.slice(5))} ~ ${esc(p.end.slice(5))}）` +
        `${p.running ? ' · 进行中' : ''}${p.count ? ` · ${p.count} 份` : ''}</option>`,
    )
    .join('');
  const running = v.periods.find((p) => p.no === v.periodNo)?.running;
  return `<div class="bl-g">
    <h4><span class="bl-no">1</span>双周报周期</h4>
    <select id="bl-period" aria-label="选择双周报周期">${opts}</select>
    <p class="bl-hint">${
      running
        ? '这一期<b>进行中</b>，多数队伍尚未提交，快报数字会偏低。'
        : '默认选中最近一个<b>已结束</b>的周期——阶段快报回顾的是已收口的一期。'
    }</p>
  </div>`;
}

/** ② 区块。⚠ 刊头 / 综述 / 结语三节**必含**，所以它们是锁着的一行，不是开关 */
function blockGroup(b: Bulletin | null): string {
  /* ⚠ 还没生成时显示**服务端的默认值**，不是一片空——见 DEFAULT_TOGGLES 的注释 */
  const row = (key: string, label: string, on: boolean): string =>
    `<button class="bl-prow${on ? ' on' : ''}" data-bl="tog|${key}" aria-pressed="${on}">
      <span class="bl-pck">${CHECK}</span><span class="bl-ptx"><b>${esc(label)}</b></span></button>`;
  const t = b?.toggles ?? DEFAULT_TOGGLES;
  return `<div class="bl-g">
    <h4><span class="bl-no">2</span>包含区块</h4>
    <div class="bl-prow on" aria-disabled="true" style="cursor:default">
      <span class="bl-pck">${CHECK}</span>
      <span class="bl-ptx"><b style="color:var(--ink-4)">刊头 · 本期综述 · 结语（必含）</b></span>
    </div>
    ${row('stats', '核心指标', t.stats)}
    ${row('teams', '各队一览', t.teams)}
    ${row('acts', '活动掠影', t.acts)}
  </div>`;
}

/**
 * ③ 照片精选：**按队伍的列表**，不是缩略图海洋（`16` §二之四）。
 * ⚠ 缩略图走 `?v=t` 规格 + 懒加载，不拉 COS 原图。
 * ⚠ 「换图」只在该队周期内有多场时才给——只有一场时换给谁？点了弹出该队全部照片直接点选（2026-11-03，原为盲循环）。
 */
function photoGroup(v: BulletinPageView, b: Bulletin | null): string {
  /* ⚠ 还没生成时，按服务端 `defaultPhotoIds()` 的同一条规则（每队最近一场）**预览**勾选状态。
     那条规则是**机械的**（按队、按时间），不含审美判断——所以它不算碰了「不做 AI 自动选图」。 */
  const preview = new Set<string>();
  if (!b) {
    const seen = new Set<string>();
    for (const c of v.candidates) {
      if (seen.has(c.teamId)) continue;
      seen.add(c.teamId);
      preview.add(c.actId);
    }
  }
  const picked = b ? new Set(b.photoIds) : preview;
  const byTeam = new Map<string, BulletinPhotoCandidate[]>();
  for (const c of v.candidates) byTeam.set(c.teamId, [...(byTeam.get(c.teamId) ?? []), c]);

  /* 每支队只列**当前选中的那一场**（没选中就列该队最近一场），列表长度 = 队伍数而不是场次数 */
  const rows = Array.from(byTeam.values())
    .map((list) => list.find((c) => picked.has(c.actId)) ?? list[0])
    .filter((c): c is BulletinPhotoCandidate => !!c)
    .map((c) => {
      const on = picked.has(c.actId);
      const more = (byTeam.get(c.teamId)?.length ?? 0) > 1;
      return `<div class="bl-prow${on ? ' on' : ' off'}">
        <button class="bl-pck" data-bl="pick|${esc(c.actId)}" aria-pressed="${on}"
          aria-label="${on ? '取消' : '选用'}${esc(c.teamName)}这张照片">${CHECK}</button>
        <span class="bl-pth">${PIC_PH}<img src="${esc(uploadApi.imgUrl(c.objectKey, 't'))}"
          alt="" loading="lazy" decoding="async" data-imgfb></span>
        <span class="bl-ptx"><b>${esc(c.teamName)}</b><s>${esc(c.date.slice(5))} ${esc(c.summary)}</s></span>
        ${when(more, `<button class="bl-pswap" data-bl="swap|${esc(c.actId)}"
          aria-label="从${esc(c.teamName)}的 ${byTeam.get(c.teamId)?.length ?? 0} 张照片里选一张">换图<span>${byTeam.get(c.teamId)?.length ?? 0}</span></button>`)}
      </div>`;
    })
    .join('');

  const cnt = picked.size;
  return `<div class="bl-g">
    <h4><span class="bl-no">3</span>照片精选<span class="bl-cnt">${cnt} / ${v.candidates.length}</span></h4>
    <p class="bl-hint" style="margin:0 0 8px">默认已按「每队封面各一张」勾选，${
      b ? '可取消，或点「换图」从该队全部照片里挑一张。' : '生成后可在这里取消或换图。'
    }</p>
    ${rows ? `<div class="bl-plist">${rows}</div>` : '<div class="bl-fact">本周期内还没有带照片的打卡记录，活动掠影将不含照片墙。</div>'}
    <p class="bl-hint">最多三行 · 每行 <span class="n">3—5</span> 张 · 至少 <span class="n">3</span> 张</p>
  </div>`;
}

/**
 * ④ 材料完整性。
 * ⚠ **中性灰**：这是陈述事实，不是待办（`16` §五之四）。
 * ⚠ 材料未齐**仍可生成**，只是标注缺失队伍。
 */
function factGroup(v: BulletinPageView): string {
  const miss = v.current?.snapshot.missing ?? [];
  const photoNote = v.missingPhotos
    ? `<div class="bl-fact" style="margin-top:8px">有 <b class="n">${v.missingPhotos}</b> 张已选照片不可用（打卡记录或照片已删除），该图位不占版面。</div>`
    : '';
  const body = !v.current
    ? '<div class="bl-fact">尚未生成快报。</div>'
    : miss.length
      ? `<div class="bl-fact">本期 <b class="n">${v.current.snapshot.stats.teams}</b> 支队伍中，
          <b>${miss.map((m) => esc(m.name)).join('、')}</b> 未提交双周报。快报仍可生成，该队亮点标注为「暂无摘录」。</div>`
      : `<div class="bl-fact">本期 <b class="n">${v.current.snapshot.stats.teams}</b> 支队伍材料齐全。</div>`;
  return `<div class="bl-g"><h4>材料完整性</h4>${body}${photoNote}</div>`;
}

/* ===================== 三、工具条与历史浮层 ===================== */

const SRC_TAG: Record<Bulletin['source'], string> = {
  ai: '<span class="bl-tag ai">智能摘要</span>',
  mixed: '<span class="bl-tag ai">部分智能摘要</span>',
  template: '<span class="bl-tag man">模板文案</span>',
};

function barHtml(v: BulletinPageView): string {
  const b = v.current;
  const busy = state.bl.busy;
  const left = b
    ? `<span class="bl-bar-l">${viewingOld(v) ? '历史版本' : '最近生成'} <b class="n">${esc(shortAt(b.createdAt))}</b>
        <span style="color:var(--line)">·</span> ${esc(b.createdByName)} ${SRC_TAG[b.source]}
        ${when(b.edited, '<span class="bl-tag man">已人工编辑</span>')}
        ${exportedTag(b.exportedAt)}</span>`
    : '<span class="bl-bar-l">这一期还没有生成过快报</span>';
  const dis = busy ? ' disabled' : '';
  return `<div class="bl-bar">
    ${left}
    ${when(
      v.history.length > 0,
      `<button class="btn" data-bl="hist" style="margin-left:auto" aria-expanded="${state.bl.hist}">
        ${IC.info}<span>历史版本 <span class="n">${v.history.length}</span></span></button>`,
    )}
    <button class="btn${b ? '' : ' primary'}" data-bl="gen"${dis}
      ${v.history.length ? '' : 'style="margin-left:auto"'}>
      ${IC.check}<span>${b ? '重新生成' : '生成快报'}</span></button>
    ${when(
      !!b,
      `<button class="btn" data-bl="png"${dis}>${IC.pic}<span>导出长图</span></button>
       <button class="btn primary" data-bl="pdf"${dis}>${IC.down}<span>导出 PDF</span></button>`,
    )}
  </div>`;
}

/** 「已导出」标记（2026-09-28）：同一期好几份时，靠它认出发出去的是哪一份 */
const exportedTag = (at: string | null | undefined): string =>
  at ? `<span class="bl-tag now">已导出 <span class="n">${esc(shortAt(at))}</span></span>` : '';

/** 历史浮层。⚠ 管理员与队长**互相都能删任意一条**（`16` §四之五），所以每行都有删除 */
function histHtml(v: BulletinPageView): string {
  if (!state.bl.hist || !v.history.length) return '';
  /* ⚠ 2026-09-28 补「查看」：`16` §〇 写的是「可查看与删除历史版本」，原先只做了删除。
     「最新」标在第一条（该期最近生成的那份），高亮条跟着纸上正在展示的那一份走。 */
  const shownId = v.current?.id;
  const rows = v.history
    .map((h, i) => {
      const shown = h.id === shownId;
      return `<div class="bl-hrow${shown ? ' cur' : ''}">
        <div class="m">
          <div class="t"><b>${esc(shortAt(h.createdAt))}</b>
            ${i === 0 ? '<span class="bl-tag now">最新</span>' : ''}
            ${shown && i !== 0 ? '<span class="bl-tag man">正在查看</span>' : ''}</div>
          ${when(
            h.edited || !!h.exportedAt,
            `<div class="tags">${when(h.edited, '<span class="bl-tag man">已人工编辑</span>')}${exportedTag(
              h.exportedAt,
            )}</div>`,
          )}
          <div class="who">${esc(h.createdByName)} 生成 · ${
            h.source === 'template' ? '模板文案' : h.source === 'ai' ? '智能摘要' : '部分智能摘要'
          }</div>
        </div>
        ${when(!shown, `<button class="tbtn" data-bl="view|${esc(h.id)}">查看</button>`)}
        <button class="tbtn danger" data-bl="del|${esc(h.id)}">删除</button>
      </div>`;
    })
    .join('');
  return `<div class="bl-hist" role="dialog" aria-label="历史版本">
    <div class="bl-hist-h"><b>历史版本</b><span>${esc(
      v.periods.find((p) => p.no === v.periodNo)?.name ?? '',
    )}</span></div>
    <div class="bl-hlist">${rows}</div>
    <div class="bl-hf">重新生成会新增一条记录，旧记录保留，可随时查看或导出。管理员和队长均可删除任意一条。</div>
  </div>`;
}

/* ===================== 四、整页 ===================== */

export function bulletinsHtml(v: BulletinPageView): string {
  /* 一个周期都没建：**正常业务态**（红线 3.5），给空态。
     ⚠ 只有管理员能建周期，「去管理后台」按钮只给管理员；队长看到的是「联系管理员」 */
  if (v.periodId == null) {
    const admin = meta.isAdmin();
    return `<div class="page">
      <div class="pagehead"><div><h1>项目快报</h1></div></div>
      ${emptyHtml(
        EIC.cal,
        '尚未创建任何周期',
        admin
          ? '快报按双周报周期生成。先在管理后台创建第 1 期，等这一期收口后再回来生成快报。'
          : '快报按双周报周期生成。项目组管理员创建第 1 期、这一期收口后，即可在这里生成快报。',
        admin ? '<button class="btn primary" data-nav="#/admin/periods">去管理后台</button>' : '',
      )}
    </div>`;
  }

  const b = v.current;
  const paper = b
    ? `<div class="bp-stage"><div class="bp-fit">${paperHtml(b, v.photos)}</div></div>`
    : emptyHtml(
        EIC.doc,
        '这一期尚未生成快报',
        v.ai.enabled
          ? '点「生成快报」后，系统会按本期数据算好各项数字，再由 AI 写成对外可发布的文案。生成一次约需半分钟。'
          : v.ai.reason,
        '<button class="btn primary" data-bl="gen">生成快报</button>',
      );

  return `<div class="page bl-page">
    <div class="pagehead">
      <div>
        <h1>项目快报</h1>
        <p class="ph-sub">按双周报周期生成训练营整体快报，文案可就地编辑，导出长图或 PDF 对外分享</p>
      </div>
    </div>

    ${when(
      !!v.stale,
      `<div class="bl-stale">
        <span class="ic">${IC.warn}</span>
        <span class="tx">本快报数据截至 <b class="n">${esc(
          v.stale ? shortAt(v.stale.asOf) : '',
        )}</b>，此后有 <b class="n">${v.stale?.changedTeams ?? 0}</b> 支队伍的双周报发生变化，建议重新生成后再导出。</span>
        <button class="tbtn" data-bl="gen">立即重新生成</button>
      </div>`,
    )}
    ${when(
      viewingOld(v),
      `<div class="bl-fact bl-viewing">
        <span>正在查看 <b class="n">${esc(v.current ? shortAt(v.current.createdAt) : '')}</b> 生成的历史版本，不是这一期最新的一份。可在此导出或编辑这一份。</span>
        <button class="tbtn" data-nav="${esc(latestHash(v))}">返回最新版本</button>
      </div>`,
    )}
    ${when(
      !v.ai.enabled || v.ai.budgetExhausted,
      `<div class="bl-fact" style="margin-bottom:12px">${esc(v.ai.reason)}</div>`,
    )}

    <div class="bl-wrap">
      <div class="bl-cfg">
        ${periodGroup(v)}
        ${blockGroup(b)}
        ${photoGroup(v, b)}
        ${factGroup(v)}
      </div>
      <div class="bl-right">
        ${barHtml(v)}
        ${histHtml(v)}
        <div class="bl-view bp-view" id="bl-view">
          ${paper}
          ${when(state.bl.busy, overlayHtml())}
        </div>
      </div>
    </div>
  </div>`;
}

/** 生成覆盖层。⚠ 步骤是后端**真实推过来的**，不是假进度（`16` §三之三 的粒度天然支持） */
function overlayHtml(): string {
  return `<div class="bl-ov" id="bl-ov" role="status">
    <h4>正在生成快报…</h4>
    <ol id="bl-steps"></ol>
    <p class="note">这一步会按本期数据算好各项数字，再逐段生成文案；约需半分钟，请不要关闭页面。</p>
  </div>`;
}

/* ===================== 五、动作 ===================== */

export interface BulletinsData {
  view: BulletinPageView;
}

/** 版本冲突与其余错误都只用 toast：这一页没有可停留的表单，错误留在原地没有意义 */
/* ⚠ 换照片时后端要为新图调一次模型补图注（2026-09-28），一次保存要几秒。
   保存中再点会带着同一个旧版本号再发一次，必然撞版本冲突——所以保存中的点击直接忽略。 */
let saving = false;

async function save(
  d: BulletinsData,
  body: Parameters<typeof api.patch>[1],
): Promise<void> {
  const b = d.view.current;
  if (!b || saving) return;
  saving = true;
  document.querySelector('.bl-plist')?.classList.add('bl-saving');
  try {
    await api.patch(b.id, { ...body, version: b.version });
    refreshCurrent();
  } catch (e) {
    toast(errText(e));
    /* ⚠ 冲突之后必须重拉：本地这份已经是旧的了，再点一次还会撞同一个版本号 */
    refreshCurrent();
  } finally {
    saving = false;
  }
}

/**
 * 生成前提示（2026-09-28）：该期**此刻**还有队伍没交双周报时先确认。
 * ⚠ 名单是后端按当前口径算好的 `view.pending`，这里不判任何东西。
 * ⚠ 交齐了就直接生成，不多弹一次。
 */
function confirmGenerate(d: BulletinsData): void {
  if (!d.view.periodId || state.bl.busy) return;
  const miss = d.view.pending;
  if (!miss.length) return void generate(d);
  confirmModal({
    title: `还有 ${miss.length} 支队伍未提交双周报`,
    okText: '仍然生成',
    html: `<b>${miss.map((m) => esc(m.name)).join('、')}</b> 目前还没有提交本期双周报。<br>
      现在生成，这几支队在快报里会标为「未提交」，并按未提交计入提交率；补交后需要重新生成。`,
    onOk: () => void generate(d),
  });
}

/** 生成 / 重新生成。⚠ 重新生成产生**新记录**，旧的原样保留 */
async function generate(d: BulletinsData): Promise<void> {
  const periodId = d.view.periodId;
  if (!periodId || state.bl.busy) return;
  state.bl.busy = true;
  state.bl.hist = false;
  refreshCurrent();

  const steps: string[] = [];
  const paint = (): void => {
    const ol = $('#bl-steps');
    if (!ol) return;
    ol.innerHTML = steps
      .map(
        (s, i) =>
          `<li class="${i === steps.length - 1 ? 'on' : 'ok'}">
            <span class="dot">${i === steps.length - 1 ? '' : IC.check}</span>${esc(s)}</li>`,
      )
      .join('');
  };

  let failed = '';
  try {
    await api.generate(periodId, (e) => {
      if (e.t === 'step') {
        steps.push(e.text);
        paint();
      } else if (e.t === 'error') {
        failed = e.message;
      }
    });
  } catch (e) {
    failed = errText(e);
  } finally {
    state.bl.busy = false;
  }
  /* ⚠ 无论成败都重拉一次：成功要显示新记录，失败也要把覆盖层收掉。
     ⚠ 在看历史版本时点了重新生成：成功后回到最新（hash 去掉版本那一段），否则纸上还是那份旧的 */
  if (!failed && location.hash !== latestHash(d.view)) go(latestHash(d.view));
  else refreshCurrent();
  if (failed) toast(failed);
  else toast('快报已生成');
}

/** 换图：弹出该队周期内的全部候选，点哪张换哪张；当前那张标出来 */
function swapPhoto(d: BulletinsData, actId: string): void {
  const b = d.view.current;
  if (!b) return;
  const cur = d.view.candidates.find((c) => c.actId === actId);
  if (!cur) return;
  const mine = d.view.candidates.filter((c) => c.teamId === cur.teamId);
  const cells = mine
    .map((c) => {
      const now = c.actId === actId;
      return `<button class="bl-sw${now ? ' now' : ''}" data-sw="${esc(c.actId)}"
        aria-label="${esc(c.date)} ${esc(c.summary)}${now ? '（当前）' : ''}">
        <span class="im">${PIC_PH}<img src="${esc(uploadApi.imgUrl(c.objectKey, 'c'))}" alt=""
          loading="lazy" decoding="async" data-imgfb>${now ? '<i>当前</i>' : ''}</span>
        <b>${esc(c.date.slice(5))}</b><s>${esc(c.summary)}</s>
      </button>`;
    })
    .join('');
  openModal({
    title: `为「${cur.teamName}」选一张照片`,
    titleMeta: `本期共 ${mine.length} 张`,
    size: 'md',
    body: `<div class="bl-swg">${cells}</div>`,
    footer: '<button class="btn" data-mclose>取消</button>',
    onMount(root) {
      root.querySelector('.bl-swg')?.addEventListener('click', (e) => {
        const next = (e.target as HTMLElement).closest<HTMLElement>('[data-sw]')?.dataset.sw;
        if (!next) return;
        closeModal(true);
        if (next === actId && b.photoIds.includes(actId)) return;
        const ids = b.photoIds.includes(actId)
          ? b.photoIds.map((x) => (x === actId ? next : x))
          : [...b.photoIds, next];
        void save(d, { version: b.version, photoIds: ids });
      });
    },
  });
}

function togglePhoto(d: BulletinsData, actId: string): void {
  const b = d.view.current;
  if (!b) return;
  const ids = b.photoIds.includes(actId)
    ? b.photoIds.filter((x) => x !== actId)
    : [...b.photoIds, actId];
  void save(d, { version: b.version, photoIds: ids });
}

function toggleBlock(d: BulletinsData, key: 'stats' | 'teams' | 'acts'): void {
  const b = d.view.current;
  if (!b) return;
  void save(d, { version: b.version, toggles: { ...b.toggles, [key]: !b.toggles[key] } });
}

function removeBulletin(d: BulletinsData, id: string): void {
  const h = d.view.history.find((x) => x.id === id);
  if (!h) return;
  confirmModal({
    title: '删除这一份快报',
    okText: '确认删除',
    danger: true,
    html: `确认删除 <b>${esc(shortAt(h.createdAt))}</b> 由 <b>${esc(h.createdByName)}</b> 生成的那一份？<br>
      删除后无法恢复。同一周期的其他记录不受影响。`,
    onOk: () => {
      void (async () => {
        try {
          await api.remove(id);
          toast('已删除');
        } catch (e) {
          toast(errText(e));
        }
        /* ⚠ 删的正是纸上这份历史版本：回到最新，不留一个指向已删记录的地址 */
        if (id === d.view.current?.id && location.hash !== latestHash(d.view)) go(latestHash(d.view));
        else refreshCurrent();
      })();
    },
  });
}

/**
 * 就地编辑：点一下变输入框，**失焦即生效**（一期同款交互）。
 * ⚠ 编辑框直接长在纸上，所以要用一个跟原文一样字号 / 行高的 textarea——
 *   `.bl-ta` 是 14 段里就有的，`font:inherit` 已经做到了这一点。
 */
function beginEdit(d: BulletinsData, host: HTMLElement): void {
  const b = d.view.current;
  if (!b || host.classList.contains('on')) return;
  const key = host.dataset.ed ?? '';
  const before = host.textContent ?? '';

  host.classList.add('on');
  const ta = document.createElement('textarea');
  ta.className = 'bl-ta';
  ta.value = before;
  ta.rows = Math.max(1, Math.ceil(before.length / 40));
  host.textContent = '';
  host.appendChild(ta);
  ta.focus();
  ta.setSelectionRange(ta.value.length, ta.value.length);

  const finish = (commit: boolean): void => {
    const next = ta.value.trim();
    host.classList.remove('on');
    if (!commit || next === before.trim()) {
      host.textContent = before;
      return;
    }
    host.textContent = next;
    void save(d, { version: b.version, sections: sectionPatch(key, next) });
  };
  ta.addEventListener('blur', () => finish(true));
  ta.addEventListener('keydown', (ev) => {
    /* ⚠ Esc 放弃、Ctrl/Cmd+Enter 提交；单独的 Enter **要能换行**——这是段落不是单行输入 */
    if (ev.key === 'Escape') {
      ev.stopPropagation();
      finish(false);
      ta.blur();
    } else if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey)) {
      ev.preventDefault();
      ta.blur();
    }
  });
}

/** 把「哪一段」翻成入参。⚠ 未知的 key 一律返回空对象，不要让它落进请求体 */
function sectionPatch(key: string, text: string): Record<string, unknown> {
  const one = { text, from: 'template' as const };
  if (key === 'title') return { title: text };
  if (key === 'intro' || key === 'dataNote' || key === 'actSummary' || key === 'closing') {
    return { [key]: one };
  }
  if (key.startsWith('hl:')) return { highlights: { [key.slice(3)]: one } };
  if (key.startsWith('cap:')) return { captions: { [key.slice(4)]: one } };
  return {};
}

/**
 * 纸比容器宽时整张等比缩。
 * ⚠ 缩的是**外面那一层**（`.bp-fit`），纸本身恒为 860px——
 *   导出要的是稳定的像素宽度，改纸的宽度会让导出物随窗口大小变。
 * ⚠ 只缩不放：容器比纸宽时保持 1，放大只会让位图糊掉。
 */
function fit(): void {
  const view = $('#bl-view');
  const box = view?.querySelector<HTMLElement>('.bp-fit');
  if (!view || !box) return;
  const avail = view.clientWidth - 40;
  box.style.setProperty('--bp-k', String(Math.min(1, avail / PAPER_W)));
  /* 缩放后元素的**布局高度不变**，父容器会留出一大截空白，所以把高度按比例压回去 */
  const paper = box.firstElementChild as HTMLElement | null;
  const k = Math.min(1, avail / PAPER_W);
  box.style.height = paper ? `${paper.offsetHeight * k}px` : '';
}

/* ===================== 六、页面模块 ===================== */

export const bulletinsPage = definePage<BulletinsData>({
  skeleton: 'detail',

  async load({ route, signal }) {
    const no = route.view === 'bulletins' ? route.no : null;
    const id = route.view === 'bulletins' ? route.id : null;
    return { view: await api.page(no, id, { signal }) };
  },

  html: (d) => bulletinsHtml(d.view),

  mounted() {
    fit();
    /* ⚠ 纸里有照片，图片是后到的——它一到高度就变了，`fit()` 算出来的高度会偏小。
       监听一次 load 事件（捕获阶段，`load` 不冒泡）重量一次即可。 */
    $('#bl-view')?.addEventListener('load', fit, { capture: true, once: true });
    const main = $('#main');
    if (main && state.bl.scroll) main.scrollTop = state.bl.scroll;
  },

  onEvent(e, d) {
    const t = e.target;
    if (!(t instanceof Element)) return;

    if (e.type === 'resize') return fit();

    if (e.type === 'change' && t.id === 'bl-period') {
      /* ⚠ 换期走 hash（守卫会改写 + 重新渲染）——**不是**就地刷新：
         周期在 hash 里，不然刷新就丢了选择，也没法把链接直接发给人（`16` §四之二）。 */
      return go(`#/bulletins/p${(t as HTMLSelectElement).value}`);
    }

    if (e.type === 'click') {
      const host = t.closest<HTMLElement>('.bl-ed');
      if (host) return beginEdit(d, host);

      const op = t.closest<HTMLElement>('[data-bl]')?.dataset.bl;
      if (!op) {
        /* 点浮层外面收起历史浮层——它是浮层不是面板 */
        if (state.bl.hist && !t.closest('.bl-hist')) {
          state.bl.hist = false;
          refreshCurrent();
        }
        return;
      }
      const [cmd, arg] = op.split('|');
      if (cmd === 'hist') {
        state.bl.hist = !state.bl.hist;
        return refreshCurrent();
      }
      if (cmd === 'gen') return confirmGenerate(d);
      if (cmd === 'tog' && arg) return toggleBlock(d, arg as 'stats' | 'teams' | 'acts');
      if (cmd === 'pick' && arg) return togglePhoto(d, arg);
      if (cmd === 'swap' && arg) return swapPhoto(d, arg);
      if (cmd === 'del' && arg) return removeBulletin(d, arg);
      if (cmd === 'view' && arg) {
        state.bl.hist = false;
        return go(`${latestHash(d.view)}/${arg}`);
      }
      if (cmd === 'png' || cmd === 'pdf') return void doExport(d, cmd);
    }

    if (e.type === 'keydown' && (e as KeyboardEvent).key === 'Enter') {
      const host = t.closest<HTMLElement>('.bl-ed');
      if (host && !host.classList.contains('on')) {
        e.preventDefault();
        return beginEdit(d, host);
      }
    }
  },
});

/** 导出。⚠ 失败要说清是哪一步失败，不要只弹「导出失败」 */
async function doExport(d: BulletinsData, kind: 'png' | 'pdf'): Promise<void> {
  const paper = $('#bp-paper');
  const b = d.view.current;
  if (!paper || !b) return;
  toast(kind === 'png' ? '正在生成长图…' : '正在生成 PDF…');
  try {
    await exportPaper(paper, `${b.sections.title}（${b.snapshot.cycle.asOf.slice(0, 10)}）`, kind);
  } catch (e) {
    toast(errText(e));
    return;
  }
  /* 导出成功后补记一笔（2026-09-28）。⚠ 留痕失败不提示：文件已经下载到本地了，
     这时弹「失败」只会让人以为导出没成功、再点一次 */
  try {
    await api.exported(b.id, kind);
    refreshCurrent();
  } catch {
    /* 静默 */
  }
}
